import moment from 'moment';
import React, { ComponentProps } from 'react';
import styled from 'styled-components';
import { TransactionState } from '../../../reducers/transaction';
import { selectUserPrivateMoney } from '../../../reducers/user-private-money';
import { formatUUID } from '../../../utils';
import FilterForm from './FilterForm';

type Props =
  | { isClearing: true; params: ClearingDetailsParams; privateMoney?: undefined }
  | {
      isClearing: false;
      params: FilterParams;
      privateMoney: PrivateMoney;
    };

type FilterParams = Partial<ComponentProps<typeof FilterForm>['filterParams']>;
type PrivateMoney = ReturnType<typeof selectUserPrivateMoney>;
type ClearingDetailsParams = TransactionState['listForm'];

type Formatters<T> = {
  [K in keyof T]: (value: T[K]) => string;
};

type FilterParamsFormatters = Omit<
  Formatters<FilterParams>,
  'transactionTypes' | 'topupMethodTypes'
>;

const FilteringWrapper = styled.div`
  margin-top: 10px;
  padding: 10px;
  border-radius: 10px;
  background-color: lightGray;
  font-weight: bold;
`;

const TOPUP_METHODS = {
  creditcard: 'クレジットカードチャージ',
  bankpay: 'Bank Payチャージ',
  'sevenbank-atm': 'セブン銀行ATMチャージ',
  cvs: 'コンビニ決済',
};

const formatOnExists =
  <T,>(formatter: (value: T) => string) =>
  (value?: T | null) =>
    value ? formatter(value) : '';

const FILTER_PARAMS_FORMATTERS: FilterParamsFormatters = {
  organization: formatOnExists((value) => `組織：${value.name}`),
  shop: formatOnExists((value) => `店舗：${value.name}`),
  terminal: formatOnExists((value) => `端末ID：${value.name}(${formatUUID(value.id)})`),
  from: formatOnExists((value) => `期間(from)：${moment(value).format('YYYY/MM/DD HH:mm:ss')}`),
  to: formatOnExists((value) => `期間(to)：${moment(value).format('YYYY/MM/DD HH:mm:ss')}`),
  transactionId: formatOnExists((value) => `取引ID：${value}`),
  customerId: formatOnExists((value) => `お客様ID：${value}`),
  customerName: formatOnExists((value) => `お客様ニックネーム：${value}`),
  description: formatOnExists((value) => `取引説明文：${value}`),
  isModified: formatOnExists((value) => (value ? 'キャンセル済のみ' : '')),
};

const formatCombinedTransactionTypes = (
  transactionTypes?: (string | null)[] | null,
  topupMethodTypes?: (string | null)[] | null
) => {
  const combinedList = [
    transactionTypes
      ?.filter((t) => t !== 'topup' || (topupMethodTypes || []).length === 0)
      ?.map((t) => (t === 'topup' ? 'チャージ' : '支払い'))
      .join('/'),
    topupMethodTypes
      ?.filter(Boolean)
      .map((t: keyof typeof TOPUP_METHODS) => TOPUP_METHODS[t])
      .join('/'),
  ]
    .filter(Boolean)
    .join(' / ');

  return combinedList ? `取引種別：${combinedList}` : '';
};

const toFilteringResult = (privateMoney: PrivateMoney, params?: FilterParams) => {
  const results = [
    `マネー：${privateMoney.name}`,
    formatCombinedTransactionTypes(params?.transactionTypes, params?.topupMethodTypes),
    ...Object.entries(params ?? {}).map(([k, v]) => FILTER_PARAMS_FORMATTERS[k]?.(v) ?? ''),
  ];

  // filter out empty strings
  return results.filter(Boolean).join(', ');
};

const toClearingFilterResult = (params: ClearingDetailsParams) => {
  const strMoneyId = `マネーID：${formatUUID(params.privateMoney)}`;
  const strOrgCode = `組織コード：${params.organization}`;
  const from = moment(params.from).format('YYYY/MM/DD');
  const to = moment(params.to).format('YYYY/MM/DD');
  const strTimeRange = `期間：${from}〜${to}`;
  const type =
    params.transactionTypes[0] === 'topup' ? '取引タイプ：チャージ' : '取引タイプ：支払い';

  return `【精算データの取引内訳表示中】 ${strMoneyId}, ${strOrgCode}, ${strTimeRange}, ${type}`;
};

const FilteringResult = ({ isClearing, params, privateMoney }: Props) => (
  <FilteringWrapper>
    {isClearing ? toClearingFilterResult(params) : toFilteringResult(privateMoney, params)}
  </FilteringWrapper>
);

export default FilteringResult;
